import { BuilderProduct, ProductPlacement, Project, Texture } from "@/store/builderSlice";
import { useGLTF, useTexture, Text, Line, Html } from '@react-three/drei';
import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from 'three';
import { actions as builderActions } from "@/store/builderSlice";
import BuilderSelect from "@/store/builderStoreSelector";
import { RapierRigidBody, RigidBody, euler, quat, vec3 } from "@react-three/rapier";
import { loadGLTF } from "@/utils/builder/floorTextureUtils";
import { GLTF } from "three/examples/jsm/loaders/GLTFLoader";
import { Select } from '@react-three/postprocessing';
import { useDispatch } from "react-redux";
import { DimensionalViewType, SurfaceType } from "@/utils/builder/builderConstants";
import { useThree } from "@react-three/fiber";
import { CollidingObjectsEventData } from "./Floor";
import ContextualMenu from "./ContextualMenu";
import { disableCollisionByProduct } from "@/utils/builder/productUtils";
import { PivotControls } from '@react-three/drei';
import BuilderSelct from "@/store/builderStoreSelector";
import { CuboidCollider } from "@react-three/rapier";

export interface ProductProps {
    product: BuilderProduct;
    productPlacement: ProductPlacement;
    roomBoundingBox: THREE.Box3;
    onDragStart: Function;
    onDragEnd: Function;
    onObjectRotateStart: Function;
    onObjectRotateEnd: Function;
    onObjectSelect: Function;
    onObjectDelete: Function;
    isActive: boolean;
    isPreviewMode: boolean;
    floorPositionY: number;
    toggleControls: Function;
    roomOffsetX: number;
    roomOffsetZ: number;
}

const productDimensionsMap: {
    [key: string]: {
        width: number;
        height: number;
        depth: number;
    }
} = {
    'MNS89653-B': {
        depth: 21.625,
        height: 19,
        width: 21.625
    },
    'MO78654-W': {
        depth: 0,
        height: 0,
        width: 0
    },
    'MRT87653-L': {
        depth: 15.375,
        height: 19.25,
        width: 15.375
    },
    'BMR56898-K': {
        depth: 74.75,
        height: 42.5,
        width: 87.75
    },
    'OT924-1': {
        depth: 26,
        height: 24,
        width: 24
    },
    'FS5705-1': {
        depth: 100,
        height: 32,
        width: 40
    },
    'FS5703-1': {
        depth: 90,
        height: 38.5,
        width: 38
    },
    'OT920-1': {
        depth: 48,
        height: 15,
        width: 35
    },
    'OT923-1': {
        depth: 12,
        height: 21,
        width: 12
    },
    'FS571-O': {
        depth: 38,
        height: 18,
        width: 38
    },
    'FS568-1': {
        depth: 32.5,
        height: 38,
        width: 35
    },
    'FS577-o': {
        depth: 19,
        height: 18,
        width: 19
    }
}

const METER_TO_INCHES_FACTOR = 39.3701;

const ProductView = (props: ProductProps) => {
    const dispatch = useDispatch();
    const productMeshRef = useRef();
    const highlightBoxRef = useRef<THREE.LineSegments | null>(null);
    const rotationArrowRef = useRef<THREE.Group | null>(null);
    const [productModel, setProductModel] = useState<undefined | any>();
    const [rotationInProgress, setRotationInProgress] = useState(false);
    const [isColliding, setIsColliding] = useState(false);
    const body = useRef<null | RapierRigidBody>(null);
    const productBoundingBox = useRef<THREE.Box3 | undefined>(undefined);
    const is2DView: boolean = BuilderSelect.ui().dimensionalView === DimensionalViewType.TWO_D;
    const isGlobalDimensionsEnabled: boolean = BuilderSelect.ui().enableDimensions;
    const [enableProductDimensions, setEnableProductDimensions] = useState(false);
    const [enableProductAxis, setEnableProductAxis] = useState(false);
    const productDimens = productDimensionsMap[props.product.productId] || props.product.productDimentions;
    const productWidth = productDimens.depth / METER_TO_INCHES_FACTOR;
    const productDepth = productDimens.width / METER_TO_INCHES_FACTOR;
    const productHeight = productDimens.height / METER_TO_INCHES_FACTOR;
    const camera = useThree((state) => state.camera);
    const canvasSize = useThree((state) => state.size);
    const isSelectedRef = useRef(props.isActive);
    const prevProdRotation = useRef<undefined | number>(undefined);
    isSelectedRef.current = props.isActive;
    const productBasePosY = props.product.isFromRoomScan ? props.productPlacement.position.y + 0.4 : props.roomBoundingBox.min.y;
    const isSnappingEnabled = useRef(false);

    const [isPillow, setIsPillow] = useState(false);
    const [pillowGroup, setPillowGroup] = useState<THREE.Group | null>(null);
    const [pivotControlsVisible, setPivotControlsVisible] = useState(false);
    const [pillowTransform, setPillowTransform] = useState({
        position: new THREE.Vector3(),
        rotation: new THREE.Quaternion()
    });

    const urlParams: { [key: string]: string } = BuilderSelct.ui().urlParams;
    const isShowcase = urlParams.showcase === 'true';

    const currentProductList = BuilderSelect.products_sku_id_list();

    // Create bounding box highlight geometry
    const createHighlightBoxGeometry = useCallback(() => {
        if (!productBoundingBox.current) return null;

        const box = productBoundingBox.current;
        const geometry = new THREE.EdgesGeometry(new THREE.BoxGeometry(
            box.max.x - box.min.x,
            box.max.y - box.min.y,
            box.max.z - box.min.z
        ));

        return geometry;
    }, [productBoundingBox.current]);

    // Create rotation arrow geometry pointing towards Z-axis
    const createRotationArrow = useCallback(() => {
        if (!productBoundingBox.current) return null;

        const box = productBoundingBox.current;
        const radius = Math.max(
            (box.max.x - box.min.x) / 2,
            (box.max.z - box.min.z) / 2
        ) + 0.3;

        const arrowGroup = new THREE.Group();
        
        // Create arrow shaft (cylinder) - pointing towards local Z-axis
        const shaftGeometry = new THREE.CylinderGeometry(0.02, 0.02, radius * 0.8, 8);
        const shaftMaterial = new THREE.MeshBasicMaterial({ color: '#5DADE2' });
        const shaft = new THREE.Mesh(shaftGeometry, shaftMaterial);
        
        // Position and rotate shaft to point along local Z-axis (forward)
        shaft.position.set(0, 0, radius * 0.4);
        shaft.rotation.x = Math.PI / 2;
        
        // Create arrowhead (cone)
        const headGeometry = new THREE.ConeGeometry(0.08, 0.15, 8);
        const headMaterial = new THREE.MeshBasicMaterial({ color: '#5DADE2' });
        const head = new THREE.Mesh(headGeometry, headMaterial);
        
        // Position arrowhead at the tip, pointing towards local Z
        head.position.set(0, 0, radius * 0.8);
        head.rotation.x = Math.PI / 2;
        
        arrowGroup.add(shaft);
        arrowGroup.add(head);
        
        return arrowGroup;
    }, [productBoundingBox.current]);

    // Update highlight box visibility and color
    const updateHighlightBox = useCallback(() => {
        if (!highlightBoxRef.current) return;

        const shouldShow = props.isActive || isColliding;
        highlightBoxRef.current.visible = shouldShow;

       if (shouldShow) {
        const color = isColliding ? '#FF0000' : '#0D908E';
        (highlightBoxRef.current.material as THREE.LineBasicMaterial).color = new THREE.Color(color);
        (highlightBoxRef.current.material as THREE.LineBasicMaterial).needsUpdate = true;
       }
    }, [props.isActive, isColliding]);

    const onProductModelLoaded = (gltf: GLTF) => {
        const scene: THREE.Group = gltf.scene;

        scene.updateWorldMatrix(true, true);
        const box = new THREE.Box3().setFromObject(scene);
        productBoundingBox.current = box;
        const prodWidth = box.max.x - box.min.x;
        const prodDepth = box.max.z - box.min.z;
        
        if (productDimens) {
            const productScale = productDimens.width / (prodWidth * METER_TO_INCHES_FACTOR);
            scene.scale.setScalar(productScale);
        }

        // Create a group for pillow meshes
        const pillowMeshes: THREE.Mesh[] = [];
        let hasPillow = false;

        scene.castShadow = true;
        scene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                
                // Check if the mesh is a pillow
                if (child.name.toLowerCase().includes('pillow')) {
                    pillowMeshes.push(child);
                    hasPillow = true;
                    
                    // Add click handler to pillow mesh
                    child.userData.isPillow = true;
                    child.addEventListener('click', (e: any) => {
                        e.stopPropagation(); // Prevent triggering the main product click
                        setPivotControlsVisible(!pivotControlsVisible);
                    });
                }
            }
        });

        // If pillow meshes found, create a group for them
        if (hasPillow && pillowMeshes.length > 0) {
            const pillowGroup = new THREE.Group();
            pillowGroup.name = 'PillowGroup';
            
            // Calculate center position for the group
            const pillowBox = new THREE.Box3();
            pillowMeshes.forEach(mesh => {
                pillowBox.expandByObject(mesh);
            });
            
            const center = new THREE.Vector3();
            pillowBox.getCenter(center);
            
            // Position the group at the center
            pillowGroup.position.copy(center);
            
            // Add all pillow meshes to the group and adjust their positions
            pillowMeshes.forEach(mesh => {
                const meshWorldPos = new THREE.Vector3();
                mesh.getWorldPosition(meshWorldPos);
                
                // Remove mesh from its current parent
                mesh.parent?.remove(mesh);
                
                // Add to pillow group and adjust position relative to group center
                pillowGroup.add(mesh);
                mesh.position.sub(center);
            });
            
            // Add the pillow group back to the scene
            scene.add(pillowGroup);
            setPillowGroup(pillowGroup);
        }

        // When adding a product update it in redux ProductsSkuIdList for sending it in blender script
        dispatch(
            builderActions.updateProductsSkuIdList({
                productId: props.product.productId // Just pass the single ID
            })
        );

        setIsPillow(hasPillow);
        dispatch(builderActions.updateResourceLoadStatus({
            name: props.product.productId,
            status: 'Success',
        }));
        setProductModel(scene);
    }

    const onProductLoadError = () => {
        dispatch(builderActions.updateResourceLoadStatus({
            name: props.product.productId,
            status: 'Failed',
        }));
    }

    useEffect(() => {
        if (props.product) {
            if (props.product.isFromRoomScan) {
                const box = new THREE.Box3().setFromObject(props.product.object3D);
                productBoundingBox.current = box;
                setProductModel(props.product.object3D);
            } else {
                dispatch(builderActions.updateResourceLoadStatus({
                    name: props.product.productId,
                    status: 'InProgress',
                }));
                loadGLTF(
                  props.product.model3dUrl
                    ? props.product.model3dUrl
                    : props.product.productMetaData.model3dUrl,
                  onProductModelLoaded,
                  onProductLoadError
                );
            }
        }
    }, [props.product]);

    // Update highlight box when active state or collision changes
    useEffect(() => {
        updateHighlightBox();
    }, [updateHighlightBox]);

    const onProductDragStart = useCallback((event: any) => {
        console.log('down....');
        const productBox = new THREE.Box3().setFromObject(productMeshRef.current!);
        props.onDragStart(body.current!, productBox);
    }, []);

    const onProductDragEnd = useCallback((event: any) => {
        console.log('up....')
        props.onDragEnd(event);
    }, []);

    const onProductRotateStart = useCallback((event: any) => {
        setRotationInProgress(true);
        props.onObjectRotateStart(body.current!, event, setRotationInProgress);
    }, []);

    const onProductRotateEnd = useCallback((event: any) => {
        prevProdRotation.current = undefined;
        setRotationInProgress(false);
        props.onObjectRotateEnd(body.current!, event);
    }, []);

    const onProductRotate = useCallback((event: any) => {
        const center = { x: props.productPlacement.position.x, z: props.productPlacement.position.z };
        
        // Calculate the angle based on world axis
        let angleInRadians = Math.atan2(
            event.point.z + props.roomOffsetZ - center.z,
            event.point.x + props.roomOffsetX - center.x
        );
        
        let angleInDegrees = (angleInRadians * 180) / Math.PI;
        angleInDegrees = angleInDegrees < 0 ? angleInDegrees + 360 : angleInDegrees;

        if (isSnappingEnabled.current) {
            // Snap rotation strictly to world axis (0, 90, 180, 270)
            angleInDegrees = Math.round(angleInDegrees / 90) * 90;
        }

        // Convert snapped angle back to radians
        angleInRadians = (angleInDegrees * Math.PI) / 180;

        // Set new rotation quaternion based on world Y-axis
        const snappedRotation = new THREE.Quaternion().setFromAxisAngle(
            new THREE.Vector3(0, 1, 0), // Y-axis
            -angleInRadians
        );

        // Apply the snapped rotation to the rigid body
        body.current!.setRotation(snappedRotation, true);

        console.log('Snapped Rotation:', angleInDegrees);
    }, [rotationInProgress]);
    
    const setActiveObject = useCallback((event: any) => {
        console.log('click....');
        const screenCoords = getRotatedRectangleVertices(props.productPlacement.position.x, props.productPlacement.position.z, productWidth, productDepth, props.productPlacement.rotation.y);
        props.onObjectSelect(body.current!, screenCoords);
    }, []);

    const deleteProduct = useCallback((event: any) => {
        dispatch(builderActions.removeProductFromProject({
            productPlacementId: props.productPlacement.id,
            surfaceType: SurfaceType.FLOOR,
            surfaceId: SurfaceType.FLOOR,
            undoable: true,
        }));
    }, [props.productPlacement.id]);

    const toggleProductDimensions = useCallback((event: any) => {
        if (!isGlobalDimensionsEnabled) {
            setEnableProductDimensions(!enableProductDimensions);
        }
    }, [isGlobalDimensionsEnabled, enableProductDimensions]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 's' || event.key === 'S') {
                isSnappingEnabled.current = true;
            }
        };

        const handleKeyUp = (event: KeyboardEvent) => {
            if (event.key === 's' || event.key === 'S') {
                isSnappingEnabled.current = false;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    const rotateIcon = useTexture('./icons/rotate-both.png');
    const deleteIcon = useTexture('./icons/trash.png');

    let prodEvents = !props.isPreviewMode ? {
        onPointerDown: (e: any) => {
            // Check if we clicked on a pillow
            if (e.object.userData?.isPillow) {
                return; // Let the pillow's own event handler deal with it
            }
            onProductDragStart(e);
        },
        onPointerUp: onProductDragEnd,
        onClick: (e: any) => {
            // Check if we clicked on a pillow
            if (e.object.userData?.isPillow) {
                return; // Let the pillow's own event handler deal with it
            }
            setActiveObject(e);
        }
    } : {};

    let rotateEvents: { [key: string]: (event: any) => void } = {};
    if (!props.isPreviewMode) {
        rotateEvents = {
            onPointerDown: onProductRotateStart,
            onPointerUp: onProductRotateEnd,
            ...(rotationInProgress && { onPointerMove: onProductRotate }),
        };
    }

    useEffect(() => {
        const handleCollidingObjects = (event: Event) => {
            const collidingObjects: string[] = (event as unknown as CollidingObjectsEventData).detail.objects;
            const productIsColliding = collidingObjects && collidingObjects.includes(props.product.productId);
            setIsColliding(productIsColliding);
        };

        window.addEventListener('collidingObjects', handleCollidingObjects);
        return () => {
            window.removeEventListener('collidingObjects', handleCollidingObjects);
        };
    }, [props.product.productId]);

    if (!productModel || !productBoundingBox.current) return <></>;

    const prodWidth = productBoundingBox.current.max.x - productBoundingBox.current.min.x;
    const prodHeight = productBoundingBox.current.max.y - productBoundingBox.current.min.y;
    const newRot = quat({
        x: props.productPlacement.rotation.x,
        y: props.productPlacement.rotation.y,
        z: props.productPlacement.rotation.z,
        w: props.productPlacement.rotation.w!,
    });
    const prodRotation = new THREE.Euler();
    prodRotation.setFromQuaternion(newRot, 'XYZ');
    const productScaleY = is2DView ? 0.025 : 1;

    function getRotatedRectangleVertices(
        centerX: number, centerY: number, width: number, height: number, rotationRadians: number) {

        const halfWidth = width / 2;
        const halfHeight = height / 2;

        // Calculate the coordinates of the four corners
        const x1 = centerX - halfWidth * Math.cos(rotationRadians) - halfHeight * Math.sin(rotationRadians);
        const y1 = centerY - halfWidth * Math.sin(rotationRadians) + halfHeight * Math.cos(rotationRadians);

        const x2 = centerX + halfWidth * Math.cos(rotationRadians) - halfHeight * Math.sin(rotationRadians);
        const y2 = centerY + halfWidth * Math.sin(rotationRadians) + halfHeight * Math.cos(rotationRadians);

        const x3 = centerX + halfWidth * Math.cos(rotationRadians) + halfHeight * Math.sin(rotationRadians);
        const y3 = centerY + halfWidth * Math.sin(rotationRadians) - halfHeight * Math.cos(rotationRadians);

        const x4 = centerX - halfWidth * Math.cos(rotationRadians) + halfHeight * Math.sin(rotationRadians);
        const y4 = centerY - halfWidth * Math.sin(rotationRadians) - halfHeight * Math.cos(rotationRadians);

        const vertices = [
            [x1, y1], // Top-left
            [x2, y2], // Top-right
            [x3, y3], // Bottom-right
            [x4, y4]  // Bottom-left
        ];

        let maxX = Number.NEGATIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        vertices.forEach(vertex => {
            const position = new THREE.Vector3(vertex[0], productBasePosY, vertex[1]);
            position.project(camera);
            const x = Math.round((0.5 + position.x / 2) * canvasSize.width);
            const y = Math.round((0.5 - position.y / 2) * canvasSize.height);
            maxX = Math.max(maxX, x);
            minY = Math.min(minY, y);
        });

        return [maxX, minY];
    }

    const renderProductDimensions = () => {
        const textWidthRotation = is2DView ? new THREE.Euler(-Math.PI / 2, 0, 0) : new THREE.Euler(0, 0, 0);
        const textDepthRotation = is2DView ? new THREE.Euler(-Math.PI / 2, 0, Math.PI / 2) : new THREE.Euler(0, Math.PI / 2, 0);
        const lineOffset = is2DView ? 0.05 : 0;

        return (
            <group>
                <group
                    visible={isGlobalDimensionsEnabled || enableProductDimensions}
                    scale={[1, productScaleY, 1]}
                >
                    {is2DView ?
                        (
                            <>
                                <Line
                                    points={[[0.1, productHeight, lineOffset + productDepth / 2], [productWidth / 2, productHeight, lineOffset + productDepth / 2]]}
                                    color="#16A085"
                                    lineWidth={2}
                                />
                                <Line
                                    points={[[-0.1, productHeight, lineOffset + productDepth / 2], [-productWidth / 2, productHeight, lineOffset + productDepth / 2]]}
                                    color="#16A085"
                                    lineWidth={2}
                                />
                                <Line
                                    points={[[productWidth / 2, productHeight, lineOffset + productDepth / 2 - 0.03], [productWidth / 2, productHeight, lineOffset + productDepth / 2 + 0.03]]}
                                    color="red"
                                    lineWidth={2}
                                />
                                <Line
                                    points={[[-productWidth / 2, productHeight, lineOffset + productDepth / 2 - 0.03], [-productWidth / 2, productHeight, lineOffset + productDepth / 2 + 0.03]]}
                                    color="red"
                                    lineWidth={2}
                                />
                            </>
                        ) :
                        <Line
                            points={[[-productWidth / 2, productHeight, productDepth / 2], [productWidth / 2, productHeight, productDepth / 2]]}
                            color="#16A085"
                            lineWidth={2}
                        />}
                    <Text
                        position={[0, productHeight + 0.05, productDepth / 2 + 0.05]}
                        rotation={textWidthRotation}
                        anchorX="center"
                        anchorY="middle"
                        color="black"
                        fontSize={0.1}
                    >
                        {Math.floor((productWidth * METER_TO_INCHES_FACTOR) / 12)}{"\'"} {Math.floor((productWidth * METER_TO_INCHES_FACTOR) % 12)}{"\""}
                    </Text>
                    <Line
                        points={[[productWidth / 2, 0, productDepth / 2], [productWidth / 2, productHeight, 0 + productDepth / 2]]}
                        color="#16A085"
                        lineWidth={2}
                    />
                    <Text
                        position={[0.1 + productWidth / 2, productHeight / 2, productDepth / 2]}
                        anchorX="center"
                        anchorY="middle"
                        color="black"
                        fontSize={0.1}
                    >
                        {Math.floor((productHeight * METER_TO_INCHES_FACTOR) / 12)}{"\'"} {Math.floor((productHeight * METER_TO_INCHES_FACTOR) % 12)}{"\""}
                    </Text>
                    {is2DView ?
                        (
                            <>
                                <Line
                                    points={[[lineOffset + productWidth / 2, productHeight, 0.1], [lineOffset + productWidth / 2, productHeight, productDepth / 2]]}
                                    color="#16A085"
                                    lineWidth={2}
                                />
                                <Line
                                    points={[[lineOffset + productWidth / 2, productHeight, -0.1], [lineOffset + productWidth / 2, productHeight, -productDepth / 2]]}
                                    color="#16A085"
                                    lineWidth={2}
                                />
                                <Line
                                    points={[[lineOffset + productWidth / 2 - 0.03, productHeight, productDepth / 2], [lineOffset + productWidth / 2 + 0.03, productHeight, productDepth / 2]]}
                                    color="red"
                                    lineWidth={2}
                                />
                                <Line
                                    points={[[lineOffset + productWidth / 2 - 0.03, productHeight, -productDepth / 2], [lineOffset + productWidth / 2 + 0.03, productHeight, -productDepth / 2]]}
                                    color="red"
                                    lineWidth={2}
                                />
                            </>
                        ) :
                        <Line
                            points={[[productWidth / 2, productHeight, -productDepth / 2], [productWidth / 2, productHeight, productDepth / 2]]}
                            color="#16A085"
                            lineWidth={2}
                        />
                    }
                    <Text
                        position={[productWidth / 2 + 0.05, productHeight + 0.03, 0]}
                        rotation={textDepthRotation}
                        anchorX="center"
                        anchorY="middle"
                        color="black"
                        fontSize={0.1}
                    >
                        {Math.floor((productDepth * METER_TO_INCHES_FACTOR) / 12)}{"\'"} {Math.floor((productDepth * METER_TO_INCHES_FACTOR) % 12)}{"\""}
                    </Text>
                </group>
            </group>
        )
    }

    let maxRadius = 0;
    let productSphere;
    productModel.traverse((child: any) => {
        if (child.isMesh && child.material) {
            const modelGeometry = child.geometry;
            modelGeometry.computeBoundingSphere();
            const boundingSphere = modelGeometry.boundingSphere;
            if (boundingSphere.radius > maxRadius) {
                productSphere = boundingSphere;
                maxRadius = boundingSphere.radius
            }
        }
    });
    const axisLength = Math.max(productWidth, productDepth) / 2 + 0.3;
    const disableCollision = disableCollisionByProduct(props.product.productTypeName);

    // Create highlight box geometry
    const highlightGeometry = createHighlightBoxGeometry();
    const rotationArrow = createRotationArrow();

    return (<>
        <RigidBody 
            name={props.product.productId}
            ref={body}
            position={[props.productPlacement.position.x, isShowcase ? -0.005 : -1.2, props.productPlacement.position.z]}
            rotation={prodRotation}
            mass={500}
            colliders={false}
            friction={0.1}
            linearDamping={0.95}
            angularDamping={0.99}
            lockRotations={true}
            userData={{
                type: 'product',
                product: props.product,
                productPlacement: props.productPlacement,
                disableCollision
            }}
        >
            <CuboidCollider
                args={[productWidth / 2, productHeight / 2, productDepth / 2]}
                position={[0, productHeight / 2, 0]}
                sensor={false}
                friction={0.5}
                restitution={0.1}
            />
            <group>
                {!isPillow &&
                    <primitive
                        ref={productMeshRef}
                        object={productModel}
                        {...prodEvents}
                        castShadow
                        scale={[1, productScaleY, 1]}
                    />
                }
                {isPillow && pillowGroup && (
                    <PivotControls
                        visible={props.isActive}
                        anchor={[0, 0, 0]}
                        depthTest={false}
                        lineWidth={2}
                        disableScaling={true}
                        axisColors={['#d9d7e5', '#0D908E', '#FE6526']}
                        scale={0.5}
                        onDrag={(localMatrix, deltaMatrix, worldMatrix, deltaWorldMatrix) => {
                            const position = new THREE.Vector3();
                            const quaternion = new THREE.Quaternion();
                            const scale = new THREE.Vector3();
                            
                            worldMatrix.decompose(position, quaternion, scale);
                            
                            setPillowTransform({
                                position: position,
                                rotation: quaternion
                            });
                        }}
                        onDragEnd={() => {
                            const worldPosition = new THREE.Vector3();
                            pillowGroup.getWorldPosition(worldPosition);
                            
                            const originalPos = props.productPlacement.position;
                            
                            const offset = {
                                x: worldPosition.x - originalPos.x,
                                y: worldPosition.y - originalPos.y,
                                z: worldPosition.z - originalPos.z
                            };
                            
                            const worldQuaternion = new THREE.Quaternion();
                            pillowGroup.getWorldQuaternion(worldQuaternion);
                            
                            dispatch(builderActions.updateProductInProject({
                                productPlacement: {
                                    id: props.productPlacement.id,
                                    pillowOffset: {
                                        x: offset.x,
                                        y: offset.y,
                                        z: offset.z
                                    },
                                    pillowRotation: {
                                        x: worldQuaternion.x,
                                        y: worldQuaternion.y,
                                        z: worldQuaternion.z,
                                        w: worldQuaternion.w,
                                    },
                                    position: originalPos,
                                    rotation: props.productPlacement.rotation
                                },
                                surfaceType: SurfaceType.FLOOR,
                                surfaceId: SurfaceType.FLOOR,
                                undoable: true
                            }));
                        }}
                    >
                        <primitive
                            ref={productMeshRef}
                            object={productModel}
                            {...prodEvents}
                            castShadow
                            scale={[1, productScaleY, 1]}
                        />
                    </PivotControls>
                )}

                {/* Bounding Box Highlight - using EdgesGeometry for clean lines */}
                {highlightGeometry && (
                    <lineSegments 
                        ref={highlightBoxRef}
                        geometry={highlightGeometry}
                        visible={props.isActive || isColliding}
                        position={[
                            productBoundingBox.current!.getCenter(new THREE.Vector3()).x,
                            productBoundingBox.current!.getCenter(new THREE.Vector3()).y,
                            productBoundingBox.current!.getCenter(new THREE.Vector3()).z
                        ]}
                    >
                        <lineBasicMaterial 
                            color={isColliding ? '#FF0000' : '#5DADE2'} 
                            linewidth={2.75}
                        />
                    </lineSegments>
                )}

                {/* Rotation Arrow Control - Always points toward local Z-axis */}
                {!isPillow && rotationArrow && (
                    <group
                        ref={rotationArrowRef}
                        visible={props.isActive && !props.isPreviewMode}
                        position={[0, productHeight + 0.1, 0]}
                        {...rotateEvents}
                    >
                        <primitive object={rotationArrow} />
                    </group>
                )}

                {!isPillow && !pillowGroup && (
                    <>
                        {renderProductDimensions()}
                    </>
                )}
            </group>
        </RigidBody>

        <Html position={[props.productPlacement.position.x + 0.5, props.floorPositionY + productHeight / 2, props.productPlacement.position.z]}>
            <div>
                <ContextualMenu
                    show={props.isActive}
                    dimensionsEnabled={isGlobalDimensionsEnabled || enableProductDimensions}
                    axisEnabled={enableProductAxis}
                    deleteProductCallback={deleteProduct}
                    toggleDimensionsModeCallback={toggleProductDimensions}
                    toggleAxisCallback={setEnableProductAxis}
                    dragStartCallback={() => { props.toggleControls(false) }}
                    dragEndCallback={() => { props.toggleControls(true) }}
                />
            </div>
        </Html>

        {/* Rotation Guide Ring - shown during rotation */}
        {rotationInProgress && (
            <mesh
                rotation={[-Math.PI / 2, 0, 0]}
                position={[props.productPlacement.position.x, productBasePosY + productHeight, props.productPlacement.position.z]}
            >
                <torusGeometry args={[0.9, 0.05, 16, 100]} />
                <meshBasicMaterial color={0xD6DBDF} opacity={0.3} transparent />
            </mesh>
        )}

        {/* Invisible interaction area for rotation */}
        {rotationInProgress && (
            <mesh
                rotation={[-Math.PI / 2, 0, 0]}
                position={[props.productPlacement.position.x, productBasePosY + productHeight, props.productPlacement.position.z]}
                onPointerMove={onProductRotate}
                visible={false}
            >
                <torusGeometry args={[1, 0.2, 16, 100]} />
                <meshBasicMaterial color={'green'} opacity={0.1} />
            </mesh>
        )}

        {/* Product Axis Helper */}
        <group 
            position={[
                props.productPlacement.position.x,
                productBasePosY,
                props.productPlacement.position.z
            ]}
            visible={enableProductAxis && props.isActive && !props.isPreviewMode}
        >
            <arrowHelper
                args={[new THREE.Vector3(1, 0, 0), new THREE.Vector3(0, 0, 0), axisLength, 0x145A32, 0.1, 0.1]}
            />
            <Line
                points={[[0, 0, 0], [axisLength, 0, 0]]}
                color="#145A32"
                lineWidth={1.5}
            />
            <arrowHelper
                args={[new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 0), productHeight + 0.3, 0x1B4F72, 0.1, 0.1]}
                visible={!is2DView}
            />
            <Line
                points={[[0, 0, 0], [0, productHeight + 0.3, 0]]}
                color="#1B4F72"
                lineWidth={1.5}
                visible={!is2DView}
            />
            <arrowHelper
                args={[new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 0, 0), axisLength, 0xC0392B, 0.1, 0.1]}
            />
            <Line
                points={[[0, 0, 0], [0, 0, axisLength]]}
                color="#C0392B"
                lineWidth={1.5}
            />
        </group>
    </>)
}

export default ProductView;